import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { BenachrichtigungenKontext } from './kontext';
import { EinstellungenTeil } from './einstellungen';
import { BenachrichtigungenSheet } from './panel';
import type { AppKennung, BenachrichtigungenApi } from './typen';

export interface BenachrichtigungenProviderProps {
  /** Diese App; Einträge anderer Apps öffnen per Seitenwechsel. */
  app: AppKennung;
  /** Dieselbe Basis wie die übrigen API-Aufrufe der App. */
  api: BenachrichtigungenApi;
  /** Pfad in dieser App öffnen, z. B. `navigate` aus react-router. */
  navigieren: (pfad: string) => void;
  /**
   * Adresse der Einstellungen in Konto, mit Rückweg in diese App, z. B.
   * `() => kontoUrl('/benachrichtigungen')`. Aufgerufen beim Anzeigen des
   * Panels, damit der Rückweg auf die gerade offene Seite zeigt.
   */
  kontoEinstellungen: () => string;
  children: ReactNode;
}

/**
 * Benachrichtigungen einer App: Zähler (alle 30 s) und das Panel mit „Neu“ und
 * „Alle“ als Sheet (am Handy von unten, am Rechner als Dialog), dazu ein Link
 * auf die Einstellungen in Konto. Liegt einmal um die AppShell, innerhalb von QueryClientProvider
 * und Router. Geöffnet wird über den Eintrag aus `useBenachrichtigungen()` im
 * Sidebar-Fuß; am Handy steht derselbe Eintrag unter „Mehr“.
 */
export function BenachrichtigungenProvider({ app, api, navigieren, kontoEinstellungen, children }: BenachrichtigungenProviderProps) {
  const [offen, setOffen] = useState(false);
  // `navigate` aus react-router ändert sich bei jeder Navigation; über die Ref
  // bleibt der Kontextwert stabil, und die Panel-Teile rendern nicht unnötig neu.
  const navigierenRef = useRef(navigieren);
  navigierenRef.current = navigieren;
  const navigierenStabil = useCallback((pfad: string) => navigierenRef.current(pfad), []);
  const kontoRef = useRef(kontoEinstellungen);
  kontoRef.current = kontoEinstellungen;
  const kontoStabil = useCallback(() => kontoRef.current(), []);
  const wert = useMemo(
    () => ({ app, api, navigieren: navigierenStabil, kontoEinstellungen: kontoStabil, offen, setOffen }),
    [app, api, navigierenStabil, kontoStabil, offen],
  );
  return (
    <BenachrichtigungenKontext.Provider value={wert}>
      {children}
      <BenachrichtigungenSheet open={offen} onOpenChange={setOffen} />
    </BenachrichtigungenKontext.Provider>
  );
}

const nichts = () => undefined;

/**
 * Nur die Einstellungen, ohne Liste, Zähler und Panel: für Konto, das sie über
 * core-api speichert (dieselben Pfade `/notifications/preferences`), aber keine
 * eigene Liste hat. Braucht keinen Provider darüber.
 */
export function BenachrichtigungsEinstellungen({ api }: { api: BenachrichtigungenApi }) {
  const wert = useMemo(() => ({ app: 'konto' as const, api, navigieren: nichts, kontoEinstellungen: () => '', offen: false, setOffen: nichts }), [api]);
  return (
    <BenachrichtigungenKontext.Provider value={wert}>
      <EinstellungenTeil aktiv />
    </BenachrichtigungenKontext.Provider>
  );
}
